import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Plus,
  Search,
  Upload,
  LayoutGrid,
  List,
  Map,
  Building2,
  MoreVertical,
  Sparkles,
  FileText,
  Filter,
  Trash2,
  UserPlus,
  ChevronDown,
  X,
  GripVertical,
  Target,
  TrendingUp,
  CalendarCheck,
  ExternalLink,
  Lock,
  Users,
  Shield,
  CheckCircle2,
  ChevronRight,
  Download
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const STAGE_COLORS = {
  new_lead: '#64748B',
  contacted: '#3B82F6',
  demo_scheduled: '#0071E3',
  demo_completed: '#F59E0B',
  qualified: '#8B5CF6',
  payment_requested: '#EC4899',
  payment_approved: '#10B981',
  closed_won: '#10B981',
  closed_lost: '#EF4444'
};

const PRODUCTS = ['EHR', 'RCM', 'Practice Management', 'CCM', 'RPM', 'APCM', 'AWV'];

// Lead Score Badge Component
const LeadScoreBadge = ({ score }) => {
  if (!score && score !== 0) return null;
  
  const getScoreColor = (s) => {
    if (s >= 70) return { bg: '#DCFCE7', text: '#166534', label: 'Hot' };
    if (s >= 40) return { bg: '#FEF3C7', text: '#92400E', label: 'Warm' };
    return { bg: '#F1F5F9', text: '#475569', label: 'Cold' };
  };
  
  const colors = getScoreColor(score);
  
  return (
    <div 
      className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold"
      style={{ backgroundColor: colors.bg, color: colors.text }}
      title={`AI Score: ${score}/100 (${colors.label})`}
    >
      <Target className="w-3 h-3" />
      {score}
    </div>
  );
};

// Sortable Kanban Card Component
const SortableKanbanCard = ({ lead, navigate, showAgent }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lead.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card 
        className={`cursor-pointer hover:shadow-md transition-shadow border-0 shadow-sm ${isDragging ? 'ring-2 ring-[#0071E3]' : ''}`}
        data-testid={`kanban-lead-${lead.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <div 
              {...listeners}
              className="cursor-grab active:cursor-grabbing mt-1 text-slate-400 hover:text-slate-600"
            >
              <GripVertical className="w-4 h-4" />
            </div>
            <div 
              className="flex items-start gap-3 flex-1"
              onClick={() => navigate(`/leads/${lead.id}`)}
            >
              <div className="w-8 h-8 rounded-full bg-[#1B365D]/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-4 h-4 text-[#1B365D]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-slate-900 truncate">{lead.organization_name}</p>
                  {lead.ai_score !== undefined && lead.ai_score !== null && (
                    <LeadScoreBadge score={lead.ai_score} />
                  )}
                </div>
                <p className="text-sm text-slate-500 truncate">{lead.contact_name}</p>
                {lead.specialty && (
                  <p className="text-xs text-slate-400 mt-1">{lead.specialty}</p>
                )}
                {showAgent && lead.owner_name && (
                  <div className="flex items-center justify-end mt-1.5">
                    <div className="w-5 h-5 rounded-full bg-[#1B365D] flex items-center justify-center" title={lead.owner_name}>
                      <span className="text-[8px] font-bold text-white">{lead.owner_name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Agent-restricted stages - only admin can move leads INTO these
const ADMIN_ONLY_STAGES = ['demo_completed', 'qualified', 'payment_requested', 'payment_approved', 'closed_won'];

// Droppable Column Component - Responsive width
const KanbanColumn = ({ stage, leads, navigate, isOver, isLocked, showAgent }) => {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 mb-3 px-2">
        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
        <h3 className="font-semibold text-slate-700 truncate text-sm">{stage.name}</h3>
        {isLocked && (
          <Lock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" title="Admin-only stage" />
        )}
        <Badge variant="secondary" className="ml-auto flex-shrink-0">{leads?.length || 0}</Badge>
      </div>
      <div 
        className={`space-y-2 min-h-[300px] max-h-[500px] overflow-y-auto p-2 rounded-xl transition-colors ${
          isLocked 
            ? 'bg-slate-100/60 border border-dashed border-slate-200'
            : isOver 
              ? 'bg-[#0071E3]/10 border-2 border-dashed border-[#0071E3]' 
              : 'bg-slate-50/50 border border-slate-200'
        }`}
        data-testid={`kanban-col-${stage.id}`}
      >
        <SortableContext items={leads?.map(l => l.id) || []} strategy={verticalListSortingStrategy}>
          {leads?.map(lead => (
            <SortableKanbanCard key={lead.id} lead={lead} navigate={navigate} showAgent={showAgent} />
          ))}
        </SortableContext>
        {(!leads || leads.length === 0) && (
          <div className="text-center py-6 text-slate-400 text-xs">
            {isLocked ? 'Admin-managed' : 'Drop leads here'}
          </div>
        )}
      </div>
    </div>
  );
};

const Leads = () => {
  const { getAuthHeaders, isAdmin, adminView } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filters, setFilters] = useState({ specialties: [], provider_types: [], states: [] });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(isAdmin ? 'byagent' : 'list');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [leadsByAgent, setLeadsByAgent] = useState([]);
  const [expandedAgentId, setExpandedAgentId] = useState(null);
  const [qualifyingLead, setQualifyingLead] = useState(null);
  const [newLead, setNewLead] = useState({
    contact_name: '',
    organization_name: '',
    email: '',
    phone: '',
    specialty: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    website: '',
    provider_type: '',
    current_ehr: '',
    notes: '',
    interested_products: []
  });

  useEffect(() => {
    fetchLeads();
    fetchStages();
    fetchFilters();
    if (isAdmin) {
      fetchAgents();
      fetchLeadsByAgent();
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [statusFilter, specialtyFilter, stateFilter]);

  const fetchLeads = async () => {
    try {
      let url = `${API_URL}/api/leads?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      if (specialtyFilter !== 'all') url += `specialty=${specialtyFilter}&`;
      if (stateFilter !== 'all') url += `state=${stateFilter}&`;
      if (searchQuery) url += `search=${searchQuery}&`;
      
      const response = await axios.get(url, getAuthHeaders());
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to load leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchStages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pipeline-stages`, getAuthHeaders());
      setStages(response.data);
    } catch (error) {
      console.error('Failed to load stages:', error);
    }
  };

  const fetchFilters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leads/filters`, getAuthHeaders());
      setFilters(response.data);
    } catch (error) {
      console.error('Failed to load filters:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/agents`, getAuthHeaders());
      setAgents(response.data);
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  };

  const fetchLeadsByAgent = async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(`${API_URL}/api/admin/leads-by-agent`, getAuthHeaders());
      setLeadsByAgent(response.data);
    } catch (error) {
      console.error('Failed to load leads by agent:', error);
    }
  };

  const handleQualifyLead = async (leadId) => {
    if (!window.confirm('Qualify this lead? Agent will be able to request payment.')) return;
    setQualifyingLead(leadId);
    try {
      await axios.post(`${API_URL}/api/leads/${leadId}/admin-qualify`, {}, getAuthHeaders());
      toast.success('Lead qualified! Agent can now request payment.');
      fetchLeads();
      fetchLeadsByAgent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to qualify lead');
    } finally {
      setQualifyingLead(null);
    }
  };

  const handleSearch = () => {
    fetchLeads();
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/leads`, newLead, getAuthHeaders());
      toast.success('Lead created successfully');
      setShowAddModal(false);
      setNewLead({
        contact_name: '',
        organization_name: '',
        email: '',
        phone: '',
        specialty: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        website: '',
        provider_type: '',
        current_ehr: '',
        notes: '',
        interested_products: []
      });
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    }
  };

  const handleUploadCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/leads/upload-csv`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`Imported ${response.data.created} leads`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      setShowUploadModal(false);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/leads/${leadId}`, { status: newStatus }, getAuthHeaders());
      toast.success('Status updated');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleEnrichLead = async (leadId) => {
    try {
      toast.info('Enriching lead with AI...');
      await axios.post(`${API_URL}/api/leads/${leadId}/enrich`, {}, getAuthHeaders());
      toast.success('Lead enriched with AI insights');
      fetchLeads();
    } catch (error) {
      toast.error('AI enrichment failed');
    }
  };

  const handleScoreLead = async (leadId) => {
    try {
      toast.info('Calculating AI score...');
      await axios.post(`${API_URL}/api/leads/${leadId}/score`, {}, getAuthHeaders());
      toast.success('Lead scored successfully');
      fetchLeads();
    } catch (error) {
      toast.error('AI scoring failed');
    }
  };

  const handleScoreAllLeads = async () => {
    try {
      toast.info('Scoring all leads...');
      const response = await axios.post(`${API_URL}/api/leads/score-all`, {}, getAuthHeaders());
      toast.success(`Scored ${response.data.scored_count} leads`);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to score leads');
    }
  };

  // Bulk Actions
  const toggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedLeads.length === filteredLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredLeads.map(l => l.id));
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedLeads.length === 0) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/leads/bulk/status`, {
        lead_ids: selectedLeads,
        new_status: newStatus
      }, getAuthHeaders());
      toast.success(response.data.message);
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update leads');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) return;
    if (!window.confirm(`Delete ${selectedLeads.length} leads? This cannot be undone.`)) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/leads/bulk/delete`, {
        lead_ids: selectedLeads
      }, getAuthHeaders());
      toast.success(response.data.message);
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete leads');
    }
  };

  const handleBulkAssign = async (newOwnerId) => {
    if (selectedLeads.length === 0) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/leads/bulk/assign`, {
        lead_ids: selectedLeads,
        new_owner_id: newOwnerId
      }, getAuthHeaders());
      toast.success(response.data.message);
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to assign leads');
    }
  };

  // Drag and Drop handlers for Kanban
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (over) {
      // Find which column this is over
      for (const stage of stages) {
        const stageLeads = leadsByStatus[stage.id] || [];
        if (stageLeads.some(l => l.id === over.id) || over.id === stage.id) {
          setOverId(stage.id);
          return;
        }
      }
    }
    setOverId(null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);
    
    if (!over) return;

    const activeLead = leads.find(l => l.id === active.id);
    if (!activeLead) return;

    // Find the target status
    let targetStatus = null;
    for (const stage of stages) {
      const stageLeads = leadsByStatus[stage.id] || [];
      if (stageLeads.some(l => l.id === over.id) || over.id === stage.id) {
        targetStatus = stage.id;
        break;
      }
    }

    // Check if the lead is dropped on a different column
    if (targetStatus && targetStatus !== activeLead.status) {
      // Agent restriction: block moves to admin-only stages
      if (!isAdmin && ADMIN_ONLY_STAGES.includes(targetStatus)) {
        toast.error('Qualification and payout stages are managed by Admin.', { duration: 4000 });
        return;
      }

      try {
        await axios.post(`${API_URL}/api/leads/kanban/move`, {
          lead_id: active.id,
          new_status: targetStatus
        }, getAuthHeaders());
        toast.success(`Lead moved to ${stages.find(s => s.id === targetStatus)?.name}`);
        fetchLeads();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to move lead');
      }
    }
  };

  const toggleProductSelection = (product) => {
    setNewLead(prev => ({
      ...prev,
      interested_products: prev.interested_products.includes(product)
        ? prev.interested_products.filter(p => p !== product)
        : [...prev.interested_products, product]
    }));
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchQuery) return true;
    return lead.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.organization_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const leadsWithCoords = filteredLeads.filter(lead => lead.latitude && lead.longitude);

  // Group leads by status for Kanban
  const leadsByStatus = stages.reduce((acc, stage) => {
    acc[stage.id] = filteredLeads.filter(lead => lead.status === stage.id);
    return acc;
  }, {});

  const handleExportLeadsCSV = async (agentId = null) => {
    try {
      const url = agentId 
        ? `${API_URL}/api/export/leads?agent_id=${agentId}` 
        : `${API_URL}/api/export/leads`;
      const response = await axios.get(url, {
        ...getAuthHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = agentId ? `leads_agent_${agentId.slice(0,8)}.csv` : 'leads_all.csv';
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(agentId ? 'Agent leads exported' : 'All leads exported');
    } catch {
      toast.error('Failed to export leads');
    }
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Admin Summary Bar or Agent Book Demo CTA */}
      {adminView ? (
        <div className="bg-white border border-[#ebebeb] rounded-xl px-5 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="admin-leads-summary">
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-[#111]">{leads.length} leads</span> across {new Set(leads.map(l => l.owner_id).filter(Boolean)).size} agents &middot; <span className="font-semibold text-emerald-600">{leads.filter(l => l.status === 'demo_scheduled').length} demos scheduled</span> this month
          </p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Filter by agent:</span>
            <select
              value={statusFilter === 'all' ? 'all' : statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600"
              data-testid="admin-agent-filter"
            >
              <option value="all">All agents</option>
              {agents.map(a => <option key={a.id || a.name} value={a.name}>{a.name}</option>)}
            </select>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-r from-[#1B365D] to-[#0071E3] rounded-2xl p-5 flex items-center justify-between shadow-lg" data-testid="book-demo-cta">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur">
              <CalendarCheck className="w-6 h-6 text-white" />
            </div>
            <div className="text-white">
              <h3 className="font-semibold text-base">Book a Demo for Your Lead</h3>
              <p className="text-sm text-white/80">Demos are the key step in qualifying leads. Schedule one today to move your pipeline forward.</p>
            </div>
          </div>
          <a href="https://www.ehiehr.com/bookdemo" target="_blank" rel="noopener noreferrer" className="flex-shrink-0" data-testid="book-demo-cta-link">
            <Button className="bg-white text-[#1B365D] hover:bg-white/90 rounded-full font-semibold px-6 shadow-md">
              <ExternalLink className="w-4 h-4 mr-2" />
              Book Demo Now
            </Button>
          </a>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1B365D] tracking-tight">Leads</h1>
          <p className="text-slate-500 mt-1">{leads.length} total leads in your pipeline</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            className="rounded-full"
            onClick={handleScoreAllLeads}
            data-testid="score-all-leads-btn"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Score All
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => handleExportLeadsCSV()}
              data-testid="export-leads-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
          <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full" data-testid="upload-csv-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload CSV
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload Leads CSV</DialogTitle>
              </DialogHeader>
              <div className="py-4">
                <p className="text-sm text-slate-500 mb-4">
                  Upload a CSV file with columns: contact_name, organization_name, email, phone, specialty, address, city, state, zip_code, website, provider_type, current_ehr, notes
                </p>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleUploadCSV}
                  data-testid="csv-file-input"
                />
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
            <DialogTrigger asChild>
              <Button className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full" data-testid="add-lead-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Add New Lead</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateLead} className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contact Name *</Label>
                    <Input
                      value={newLead.contact_name}
                      onChange={(e) => setNewLead({ ...newLead, contact_name: e.target.value })}
                      placeholder="Dr. John Smith"
                      required
                      data-testid="lead-contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Organization Name *</Label>
                    <Input
                      value={newLead.organization_name}
                      onChange={(e) => setNewLead({ ...newLead, organization_name: e.target.value })}
                      placeholder="ABC Medical Group"
                      required
                      data-testid="lead-organization-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newLead.email}
                      onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                      placeholder="doctor@practice.com"
                      data-testid="lead-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={newLead.phone}
                      onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      data-testid="lead-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Specialty</Label>
                    <Input
                      value={newLead.specialty}
                      onChange={(e) => setNewLead({ ...newLead, specialty: e.target.value })}
                      placeholder="Family Medicine"
                      data-testid="lead-specialty"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Provider Type</Label>
                    <Input
                      value={newLead.provider_type}
                      onChange={(e) => setNewLead({ ...newLead, provider_type: e.target.value })}
                      placeholder="Physician Group"
                      data-testid="lead-provider-type"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Address</Label>
                    <Input
                      value={newLead.address}
                      onChange={(e) => setNewLead({ ...newLead, address: e.target.value })}
                      placeholder="123 Medical Center Dr"
                      data-testid="lead-address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={newLead.city}
                      onChange={(e) => setNewLead({ ...newLead, city: e.target.value })}
                      placeholder="Austin"
                      data-testid="lead-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={newLead.state}
                      onChange={(e) => setNewLead({ ...newLead, state: e.target.value })}
                      placeholder="TX"
                      data-testid="lead-state"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ZIP Code</Label>
                    <Input
                      value={newLead.zip_code}
                      onChange={(e) => setNewLead({ ...newLead, zip_code: e.target.value })}
                      placeholder="78701"
                      data-testid="lead-zip"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input
                      value={newLead.website}
                      onChange={(e) => setNewLead({ ...newLead, website: e.target.value })}
                      placeholder="https://practice.com"
                      data-testid="lead-website"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Current EHR</Label>
                    <Input
                      value={newLead.current_ehr}
                      onChange={(e) => setNewLead({ ...newLead, current_ehr: e.target.value })}
                      placeholder="Epic, Athena, etc."
                      data-testid="lead-ehr"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Interested Products</Label>
                    <div className="flex flex-wrap gap-2">
                      {PRODUCTS.map(product => (
                        <Badge
                          key={product}
                          variant={newLead.interested_products.includes(product) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => toggleProductSelection(product)}
                        >
                          {product}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={newLead.notes}
                      onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                      placeholder="Additional notes about this lead..."
                      data-testid="lead-notes"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#1B365D] hover:bg-[#1B365D]/90" data-testid="submit-lead-btn">
                    Create Lead
                  </Button>
                </div>
                
                {/* Book Demo Section */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="bg-gradient-to-r from-[#1B365D]/5 to-[#0071E3]/5 rounded-xl p-4">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-[#1B365D] rounded-xl">
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-[#1B365D] mb-1">Book a Demo for Your Lead</h4>
                        <p className="text-sm text-gray-600 mb-3">
                          After adding the lead, direct them to our demo booking page or schedule on their behalf.
                        </p>
                        <a 
                          href="https://www.ehiehr.com/bookdemo" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2"
                        >
                          <Button 
                            type="button"
                            className="bg-[#0071E3] hover:bg-[#0071E3]/90 text-white rounded-full"
                            data-testid="book-demo-btn"
                          >
                            <Target className="w-4 h-4 mr-2" />
                            Book Demo Appointment
                          </Button>
                        </a>
                        <p className="text-xs text-gray-500 mt-2">
                          Opens Enable Healthcare's official demo booking page
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters & View Toggle */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search leads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 rounded-full border-slate-200"
                data-testid="search-leads"
              />
            </div>
            <Button 
              variant="outline" 
              className="rounded-full"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {(statusFilter !== 'all' || specialtyFilter !== 'all' || stateFilter !== 'all') && (
                <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 flex items-center justify-center">
                  {[statusFilter !== 'all', specialtyFilter !== 'all', stateFilter !== 'all'].filter(Boolean).length}
                </Badge>
              )}
            </Button>
          </div>

          <div className="flex items-center gap-2">
            {selectedLeads.length > 0 && (
              <div className="flex items-center gap-2 mr-4">
                <span className="text-sm text-slate-500">{selectedLeads.length} selected</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-full">
                      Bulk Actions <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem className="font-medium text-slate-500" disabled>
                      Change Status
                    </DropdownMenuItem>
                    {stages.map(stage => (
                      <DropdownMenuItem 
                        key={stage.id}
                        onClick={() => handleBulkStatusChange(stage.id)}
                      >
                        <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: stage.color }} />
                        {stage.name}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    {isAdmin && (
                      <>
                        <DropdownMenuItem className="font-medium text-slate-500" disabled>
                          Assign To
                        </DropdownMenuItem>
                        {agents.map(agent => (
                          <DropdownMenuItem 
                            key={agent.id}
                            onClick={() => handleBulkAssign(agent.id)}
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            {agent.name}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem 
                      onClick={handleBulkDelete}
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Selected
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedLeads([])}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            {/* View Toggle Buttons - More prominent */}
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                onClick={() => setViewMode('list')}
                className={`rounded-full ${viewMode === 'list' ? 'bg-[#1B365D] hover:bg-[#1B365D]/90' : ''}`}
                data-testid="view-list"
              >
                <List className="w-4 h-4 mr-2" />
                List
              </Button>
              <Button
                variant={viewMode === 'kanban' ? 'default' : 'outline'}
                onClick={() => setViewMode('kanban')}
                className={`rounded-full ${viewMode === 'kanban' ? 'bg-[#1B365D] hover:bg-[#1B365D]/90' : ''}`}
                data-testid="view-kanban"
              >
                <LayoutGrid className="w-4 h-4 mr-2" />
                Pipeline
              </Button>
              {isAdmin && (
                <Button
                  variant={viewMode === 'byagent' ? 'default' : 'outline'}
                  onClick={() => { setViewMode('byagent'); fetchLeadsByAgent(); }}
                  className={`rounded-full ${viewMode === 'byagent' ? 'bg-[#1B365D] hover:bg-[#1B365D]/90' : ''}`}
                  data-testid="view-byagent"
                >
                  <Users className="w-4 h-4 mr-2" />
                  By Agent
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Advanced Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <Card className="border-0 shadow-sm bg-slate-50">
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">Status</Label>
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger data-testid="status-filter">
                          <SelectValue placeholder="All Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          {stages.map(stage => (
                            <SelectItem key={stage.id} value={stage.id}>
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                                {stage.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">Specialty</Label>
                      <Select value={specialtyFilter} onValueChange={setSpecialtyFilter}>
                        <SelectTrigger data-testid="specialty-filter">
                          <SelectValue placeholder="All Specialties" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Specialties</SelectItem>
                          {filters.specialties.map(spec => (
                            <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">State</Label>
                      <Select value={stateFilter} onValueChange={setStateFilter}>
                        <SelectTrigger data-testid="state-filter">
                          <SelectValue placeholder="All States" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All States</SelectItem>
                          {filters.states.map(state => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setStatusFilter('all');
                          setSpecialtyFilter('all');
                          setStateFilter('all');
                        }}
                        className="w-full"
                      >
                        Clear Filters
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-slate-500">Loading leads...</div>
          </div>
        ) : viewMode === 'list' ? (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card className="border-0 shadow-apple">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-100">
                      <tr>
                        <th className="text-left py-4 px-4 w-10">
                          <Checkbox 
                            checked={selectedLeads.length === filteredLeads.length && filteredLeads.length > 0}
                            onCheckedChange={toggleSelectAll}
                            data-testid="select-all-leads"
                          />
                        </th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-4">Organization</th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-4">Contact</th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-4">Specialty</th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-4">Location</th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-4">Score</th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-4">Status</th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredLeads.map(lead => (
                        <tr 
                          key={lead.id} 
                          className={`hover:bg-slate-50 cursor-pointer transition-colors ${selectedLeads.includes(lead.id) ? 'bg-blue-50' : ''}`}
                          data-testid={`lead-row-${lead.id}`}
                        >
                          <td className="py-4 px-4" onClick={(e) => e.stopPropagation()}>
                            <Checkbox 
                              checked={selectedLeads.includes(lead.id)}
                              onCheckedChange={() => toggleLeadSelection(lead.id)}
                            />
                          </td>
                          <td className="py-4 px-4" onClick={() => navigate(`/leads/${lead.id}`)}>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-[#1B365D]/10 flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-[#1B365D]" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{lead.organization_name}</p>
                                {lead.email && <p className="text-sm text-slate-500">{lead.email}</p>}
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4" onClick={() => navigate(`/leads/${lead.id}`)}>
                            <p className="text-slate-900">{lead.contact_name}</p>
                            {lead.phone && <p className="text-sm text-slate-500">{lead.phone}</p>}
                          </td>
                          <td className="py-4 px-4" onClick={() => navigate(`/leads/${lead.id}`)}>
                            <p className="text-slate-600">{lead.specialty || '-'}</p>
                          </td>
                          <td className="py-4 px-4" onClick={() => navigate(`/leads/${lead.id}`)}>
                            <p className="text-slate-600">{[lead.city, lead.state].filter(Boolean).join(', ') || '-'}</p>
                          </td>
                          <td className="py-4 px-4" onClick={() => navigate(`/leads/${lead.id}`)}>
                            {lead.ai_score !== undefined && lead.ai_score !== null ? (
                              <LeadScoreBadge score={lead.ai_score} />
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </td>
                          <td className="py-4 px-4" onClick={() => navigate(`/leads/${lead.id}`)}>
                            <Badge 
                              style={{ backgroundColor: STAGE_COLORS[lead.status] + '20', color: STAGE_COLORS[lead.status] }}
                              className="capitalize font-medium"
                            >
                              {lead.status?.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="py-4 px-4" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}`)}>
                                  <FileText className="w-4 h-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEnrichLead(lead.id)}>
                                  <Sparkles className="w-4 h-4 mr-2" />
                                  AI Enrich
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleScoreLead(lead.id)}>
                                  <Target className="w-4 h-4 mr-2" />
                                  AI Score
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {stages.map(stage => (
                                  <DropdownMenuItem 
                                    key={stage.id}
                                    onClick={() => handleStatusChange(lead.id, stage.id)}
                                  >
                                    <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: stage.color }} />
                                    Move to {stage.name}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredLeads.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      No leads found. Create your first lead to get started.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ) : viewMode === 'kanban' ? (
          <motion.div
            key="kanban"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
            >
              {/* Responsive Kanban Grid - adapts to all screen sizes */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
                {stages.map(stage => (
                  <KanbanColumn
                    key={stage.id}
                    stage={stage}
                    leads={leadsByStatus[stage.id] || []}
                    navigate={navigate}
                    isOver={overId === stage.id}
                    isLocked={!isAdmin && ADMIN_ONLY_STAGES.includes(stage.id)}
                    showAgent={adminView}
                  />
                ))}
              </div>
              <DragOverlay>
                {activeId ? (
                  <Card className="w-64 shadow-xl border-0 ring-2 ring-[#0071E3]">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#1B365D]/10 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-[#1B365D]" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">
                            {leads.find(l => l.id === activeId)?.organization_name}
                          </p>
                          <p className="text-sm text-slate-500 truncate">
                            {leads.find(l => l.id === activeId)?.contact_name}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : null}
              </DragOverlay>
            </DndContext>
          </motion.div>
        ) : viewMode === 'byagent' && isAdmin ? (
          <motion.div
            key="byagent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="space-y-4" data-testid="leads-by-agent-view">
              {leadsByAgent.length === 0 ? (
                <Card className="border-0 shadow-apple">
                  <CardContent className="p-12 text-center text-slate-400">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p>No agent data available</p>
                  </CardContent>
                </Card>
              ) : (
                leadsByAgent.map(agentGroup => (
                  <Card key={agentGroup.agent_id} className="border-0 shadow-apple overflow-hidden" data-testid={`agent-group-${agentGroup.agent_id}`}>
                    <div
                      className="p-5 cursor-pointer hover:bg-slate-50/50 transition-colors"
                      onClick={() => setExpandedAgentId(expandedAgentId === agentGroup.agent_id ? null : agentGroup.agent_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-11 h-11 rounded-full bg-[#1B365D] flex items-center justify-center text-white font-bold text-lg">
                            {agentGroup.agent_name?.charAt(0)?.toUpperCase() || 'A'}
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{agentGroup.agent_name}</h3>
                            <p className="text-sm text-slate-500">{agentGroup.agent_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-400 hover:text-[#1B365D] h-8 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportLeadsCSV(agentGroup.agent_id);
                            }}
                            data-testid={`export-agent-leads-${agentGroup.agent_id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Badge variant="secondary" className="text-sm">{agentGroup.total_leads} leads</Badge>
                          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedAgentId === agentGroup.agent_id ? 'rotate-90' : ''}`} />
                        </div>
                      </div>
                    </div>
                    {expandedAgentId === agentGroup.agent_id && (
                      <div className="border-t border-slate-100">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Lead</th>
                                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Contact</th>
                                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Status</th>
                                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {agentGroup.leads.map(lead => (
                                <tr key={lead.id} className="hover:bg-slate-50" data-testid={`agent-lead-${lead.id}`}>
                                  <td className="py-3 px-4">
                                    <p className="font-medium text-slate-900 cursor-pointer hover:text-[#0071E3]" onClick={() => navigate(`/leads/${lead.id}`)}>
                                      {lead.organization_name}
                                    </p>
                                    <p className="text-xs text-slate-400">{lead.specialty}</p>
                                  </td>
                                  <td className="py-3 px-4">
                                    <p className="text-sm text-slate-700">{lead.contact_name}</p>
                                    <p className="text-xs text-slate-400">{lead.email}</p>
                                  </td>
                                  <td className="py-3 px-4">
                                    <Badge
                                      className="capitalize text-white text-xs"
                                      style={{ backgroundColor: STAGE_COLORS[lead.status] || '#64748B' }}
                                    >
                                      {lead.status?.replace(/_/g, ' ')}
                                    </Badge>
                                  </td>
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => navigate(`/leads/${lead.id}`)}
                                        className="text-slate-500"
                                      >
                                        View
                                      </Button>
                                      {['new_lead', 'contacted', 'demo_scheduled', 'demo_completed'].includes(lead.status) && (
                                        <Button
                                          size="sm"
                                          className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-full"
                                          onClick={() => handleQualifyLead(lead.id)}
                                          disabled={qualifyingLead === lead.id}
                                          data-testid={`qualify-lead-${lead.id}`}
                                        >
                                          {qualifyingLead === lead.id ? (
                                            <span className="animate-spin mr-1">...</span>
                                          ) : (
                                            <Shield className="w-3 h-3 mr-1" />
                                          )}
                                          Qualify
                                        </Button>
                                      )}
                                      {lead.status === 'qualified' && (
                                        <Badge className="bg-purple-100 text-purple-700">
                                          <CheckCircle2 className="w-3 h-3 mr-1" />
                                          Qualified
                                        </Badge>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </Card>
                ))
              )}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default Leads;
